import {useMemo, useState} from 'react';
import {nanoid} from 'nanoid/non-secure';

//#region tipos e interfaces
export type StackDictionaryManagerPush<C> = (content?: C | null, customId?: string) => string;
export type StackDictionaryManagerPop = (id?: string | null, where?: string) => void;
export interface StackDictionaryManager<C> {
   hasItems: boolean;
   push: StackDictionaryManagerPush<C>;
   pop: StackDictionaryManagerPop;
   popAll: () => void;
   toggleStack: (isAdding: boolean) => void;
   lastOpendId: string | null;
   lastContent?: C | null;
   stackDictionary: StackDictionary<C> | null;
}
export interface StackDictionaryItem<C> {
   id: string;
   order: number;
   content: C | null;
}
export interface StackDictionary<C> {
   [k: string]: StackDictionaryItem<C>;
}
export type MapStackDicManagerFunction<C, R> = (dictionary: StackDictionaryManager<C>) => R;
//#endregion
//#region variables y funciones auxiliares
function getLastOrderStackItemKey<C>(stackDic: StackDictionary<C> | null) {
   if (stackDic == null) {
      return null;
   }
   const keys = Object.keys(stackDic);
   switch (keys.length) {
      case 0:
         return null;
      case 1:
         return stackDic[keys[0]].id;
      default:
         return keys.reduce((key, curr) => {
            const max = Math.max(stackDic[key].order, stackDic[curr].order, -Infinity);
            if (max === stackDic[key].order || max === -Infinity) {
               return key;
            }
            return curr;
         });
   }
}
function getIdToDelete<C>(
   id?: string | null,
   lastId?: string | null,
   where?: string,
   getItemWhere?: (content: C) => string,

   itemList?: StackDictionaryItem<C>[] | null
) {
   //se retorna el id si es valido
   if (id && id.trim() !== '') {
      return id;
   }
   //si es posible buscar el id en una lista valida en base al parametros where y la funcion getItemWhere se realiza la busqueda correspondiente
   if (where && getItemWhere && itemList && itemList.length > 0) {
      const trimmed = where.trim();
      const whereItem = itemList.find((item) => {
         if (item.content) {
            return trimmed === getItemWhere(item.content).trim();
         }
         return false;
      });
      if (whereItem) {
         return whereItem.id;
      }
      return null;
   }
   //si no hay forma de identificar un item especifico y existe un elemento se retorna el ultimo elemento
   if (lastId) {
      return lastId;
   }
   return null;
}
//#endregion

/**
 *
 * @param mapStackManager Funcion de conversion del retorno de tipo `StackDictionaryManager<C>` a tipo `R`
 * @returns Objeto de tipo `R` o tipo `StackDictionaryManager<C>` en caso no se haya proporcionado `mapFunction`
 */
export default function useStackDictionary<C, R = StackDictionaryManager<C>>(
   mapStackManager?: MapStackDicManagerFunction<C, R>,
   getItemWhere?: (content: C) => string
) {
   const [stackDictionary, setStackDictionary] = useState<StackDictionary<C> | null>(null);

   return useMemo<StackDictionaryManager<C> | R>(() => {
      const lastOpendId = getLastOrderStackItemKey(stackDictionary);

      function push(content?: C | null, customId?: string) {
         const id: string = customId && customId.trim() != '' ? customId.trim() : nanoid(5);
         setStackDictionary((prev) => {
            let newStackDic: StackDictionary<C> | null;
            if (!prev) {
               newStackDic = {};
            } else {
               newStackDic = {...prev};
            }
            newStackDic[id] = {
               id,
               order: lastOpendId && stackDictionary ? stackDictionary[lastOpendId].order + 1 : 0,
               content: content || null
            };
            return newStackDic;
         });
         return id;
      }
      function pop(id?: string | null, where?: string) {
         //determinar el id a eliminar
         let idToDelete: string | null = getIdToDelete(
            id,
            lastOpendId,
            where,
            getItemWhere,
            stackDictionary ? Object.values(stackDictionary) : null
         );
         if (!idToDelete) {
            return;
         }
         //============================
         setStackDictionary((prev) => {
            //inicializar nuevo stack
            let newStackDic;
            if (!prev) {
               newStackDic = {};
            } else {
               newStackDic = {...prev};
            }
            //eliminar id encontrado
            if (idToDelete != null) {
               delete newStackDic[idToDelete];
            }
            return newStackDic;
         });
      }

      const newStackManager: StackDictionaryManager<C> = {
         hasItems: Boolean(stackDictionary && Object.keys(stackDictionary).length > 0),
         push: push,
         pop: pop,
         popAll() {
            setStackDictionary({});
         },
         toggleStack(isAdding: boolean = true) {
            if (isAdding) {
               push();
            } else {
               pop();
            }
         },
         lastOpendId,
         lastContent: lastOpendId && stackDictionary ? stackDictionary[lastOpendId].content : null,
         stackDictionary: stackDictionary
      };
      if (!mapStackManager) {
         return newStackManager;
      }
      return mapStackManager(newStackManager);
   }, [stackDictionary]);
}
